import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { authAPI } from '@/lib/api';
import { getErrorMessage } from '@/lib/utils';
import { t, LANGUAGES } from '@/lib/i18n';
import { toast } from 'sonner';
import { Globe, Phone, Shield } from 'lucide-react';

export default function LoginPage({ onLogin }) {
  const [mode, setMode] = useState('admin'); // 'admin' or 'employee'
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [empLoginData, setEmpLoginData] = useState({ mobile: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [lang, setLang] = useState(() => localStorage.getItem('emp_lang') || 'en');

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await authAPI.login(loginData);
      toast.success('Login successful!');
      onLogin(response.data.token, response.data.user);
    } catch (error) { toast.error(getErrorMessage(error)); }
    finally { setLoading(false); }
  };

  const handleEmpLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await authAPI.employeeLogin(empLoginData);
      toast.success(t('loginSuccess', lang));
      onLogin(response.data.token, response.data.user);
    } catch (error) { toast.error(getErrorMessage(error)); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-900 p-6" style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1611495897598-4cfcb4767483?crop=entropy&cs=srgb&fm=jpg&q=85)', backgroundSize: 'cover', backgroundPosition: 'center' }}>
      <div className="absolute inset-0 bg-slate-900/85 backdrop-blur-sm"></div>
      <div className="relative z-10 w-full max-w-md">
        <div className="text-center mb-6">
          <h1 className="text-5xl font-heading font-bold text-indigo-400 tracking-tight uppercase mb-2">GTNEPL ERP</h1>
          <p className="text-slate-300 text-sm uppercase tracking-widest">Management System</p>
        </div>

        {/* Mode Toggle */}
        <div className="flex justify-center gap-2 mb-4">
          <button
            onClick={() => setMode('admin')}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-bold transition-colors ${mode === 'admin' ? 'bg-indigo-500 text-white' : 'bg-white/10 text-slate-300 hover:bg-white/20'}`}
            data-testid="admin-mode-btn"
          >
            <Shield className="w-4 h-4" /> Admin
          </button>
          <button
            onClick={() => setMode('employee')}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-bold transition-colors ${mode === 'employee' ? 'bg-emerald-500 text-white' : 'bg-white/10 text-slate-300 hover:bg-white/20'}`}
            data-testid="employee-mode-btn"
          >
            <Phone className="w-4 h-4" /> {t('employeePortal', lang)}
          </button>
        </div>

        {/* Admin Login/Register */}
        {mode === 'admin' && (
          <Card className="border-slate-700 bg-white/95 backdrop-blur-sm">
                <CardHeader>
                  <CardTitle>Welcome Back</CardTitle>
                  <CardDescription>Enter your credentials to access your account</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleLogin} className="space-y-4">
                    <div>
                      <Label htmlFor="login-email">Email</Label>
                      <Input id="login-email" data-testid="login-email" type="email" placeholder="your@email.com" value={loginData.email} onChange={(e) => setLoginData({...loginData, email: e.target.value.trim()})} autoCapitalize="none" autoCorrect="off" required className="mt-1" />
                    </div>
                    <div>
                      <Label htmlFor="login-password">Password</Label>
                      <Input id="login-password" data-testid="login-password" type="password" placeholder="••••••••" value={loginData.password} onChange={(e) => setLoginData({...loginData, password: e.target.value})} required className="mt-1" />
                    </div>
                    <Button type="submit" data-testid="login-submit" className="w-full" disabled={loading}>{loading ? 'Logging in...' : 'Login'}</Button>
                  </form>
                </CardContent>
          </Card>
        )}

        {/* Employee Login/Register */}
        {mode === 'employee' && (
          <>
            {/* Language selector */}
            <div className="flex justify-end mb-2">
              <div className="flex items-center gap-1.5 bg-white/10 rounded-md px-2 py-1">
                <Globe className="w-3.5 h-3.5 text-slate-300" />
                <select value={lang} onChange={(e) => { setLang(e.target.value); localStorage.setItem('emp_lang', e.target.value); }} className="bg-transparent text-slate-300 text-xs border-none outline-none" data-testid="emp-lang-select">
                  {LANGUAGES.map(l => <option key={l.code} value={l.code} className="bg-slate-800">{l.label}</option>)}
                </select>
              </div>
            </div>
            <Card className="border-emerald-500/30 bg-white/95 backdrop-blur-sm">
                  <CardHeader>
                    <CardTitle>{t('loginTitle', lang)}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handleEmpLogin} className="space-y-4">
                      <div>
                        <Label>{t('mobileNumber', lang)}</Label>
                        <Input data-testid="emp-login-mobile" type="tel" placeholder="9876543210" value={empLoginData.mobile} onChange={(e) => setEmpLoginData({...empLoginData, mobile: e.target.value})} required className="mt-1" />
                      </div>
                      <div>
                        <Label>{t('password', lang)}</Label>
                        <Input data-testid="emp-login-password" type="password" placeholder="••••••••" value={empLoginData.password} onChange={(e) => setEmpLoginData({...empLoginData, password: e.target.value})} required className="mt-1" />
                      </div>
                      <Button type="submit" data-testid="emp-login-submit" className="w-full bg-emerald-600 hover:bg-emerald-700" disabled={loading}>{loading ? '...' : t('login', lang)}</Button>
                    </form>
                    <p className="text-xs text-muted-foreground text-center mt-4" data-testid="emp-no-account-hint">{t('askAdmin', lang)}</p>
                  </CardContent>
            </Card>
          </>
        )}
      </div>
    </div>
  );
}
